<template>
    <DashboardContentLayout class="space-y-6" :heading="$t('views.weekOverview.header.heading')" has-pig-type-picker has-period-picker>
        <!-- Farm picker: mutually exclusive, one pill per farm in scope (not the top-level
             multi-select LocationPicker, which is a different, hierarchical filter). -->
        <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div class="flex min-w-0 flex-1 items-center gap-3">
                <span class="shrink-0 font-semibold">{{ $t('views.weekOverview.farmPicker.label') }}:</span>
                <FarmButtonPicker v-if="validFarms.length" :farms="validFarms" v-model="selectedFarmId" />
                <span v-else class="text-sm text-gray-400">{{ $t('views.weekOverview.farmPicker.noFarms') }}</span>
            </div>
        </div>

        <div v-if="selectedFarmId === null" class="rounded-lg border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-400">
            {{ $t('views.weekOverview.texts.selectFarm') }}
        </div>

        <div v-else-if="isLoading" class="flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-16">
            <Spinner class="h-10 w-10" />
            <span class="text-sm text-gray-500">{{ $t('views.weekOverview.texts.loading') }}</span>
        </div>

        <div v-else-if="!inRangeBatches.length" class="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-400">
            {{ $t('views.weekOverview.texts.noData') }}
        </div>

        <template v-else>
            <!-- Accumulated block -->
            <div class="rounded-lg border border-gray-200 bg-white p-4">
                <div class="mb-3 text-sm font-semibold text-gray-700">{{ $t('views.weekOverview.accumulated.title') }}</div>
                <div class="flex flex-wrap divide-x divide-gray-200">
                    <AccumulatedStat :label="$t('views.weekOverview.accumulated.numberOfBatches')" :value="accumulated.numberOfBatches" />
                    <AccumulatedStat :label="$t('views.weekOverview.accumulated.totalInserted')" :value="accumulated.totalInserted" />
                    <!-- Weaners leave by sale/transfer, not slaughter: the sold/transferred total takes the slaughtered stat's place. -->
                    <AccumulatedStat v-if="!isWeaners" :label="$t('views.weekOverview.accumulated.totalSlaughtered')" :value="accumulated.totalSlaughtered" />
                    <AccumulatedStat :label="$t('views.weekOverview.accumulated.totalDead')" :value="accumulated.totalDead" />
                    <AccumulatedStat :label="$t('views.weekOverview.accumulated.totalSoldAndTransferred')" :value="accumulated.totalSoldAndTransferred" />
                    <AccumulatedStat :label="$t('views.weekOverview.accumulated.averageDead')" :value="accumulated.averageDeadPercent" suffix="%" />
                    <AccumulatedStat :label="$t(isWeaners ? 'views.weekOverview.accumulated.averageLiveExitWeight' : 'views.weekOverview.accumulated.averageExitWeight')" :value="accumulated.averageExitWeight" suffix="kg" />
                </div>
            </div>

            <!-- Batch (WeekData) selector -->
            <div class="flex items-center gap-x-3 rounded-lg border border-gray-200 bg-white p-3">
                <button
                    type="button"
                    class="rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                    :disabled="selectedBatchIndex <= 0"
                    :title="$t('views.weekOverview.batchSelector.previous')"
                    @click="selectedBatchIndex -= 1"
                >
                    <ChevronLeft class="h-4 w-4" aria-hidden="true" />
                </button>
                <select v-model.number="selectedBatchIndex" class="flex-1 rounded-md border border-gray-200 py-1.5 text-sm">
                    <option v-for="(batch, index) in inRangeBatches" :key="batch.mondayOfWeek" :value="index">
                        {{ $t('views.weekOverview.batchSelector.label') }} - {{ formatDate(batch.mondayOfWeek) }} – {{ formatDate(sundayOf(batch.mondayOfWeek)) }}
                    </option>
                </select>
                <button
                    type="button"
                    class="rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                    :disabled="selectedBatchIndex >= inRangeBatches.length - 1"
                    :title="$t('views.weekOverview.batchSelector.next')"
                    @click="selectedBatchIndex += 1"
                >
                    <ChevronRight class="h-4 w-4" aria-hidden="true" />
                </button>
            </div>

            <template v-if="selectedBatch">
                <!-- KPI summary for the selected batch -->
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <WeekKpi :label="$t('views.weekOverview.summary.totalInserted')" :value="selectedBatch.totalInserted" :suffix="$t('views.weekOverview.texts.pcsUnit')" :icon-id="kpiIcons?.totalInsertedIconId" />
                    <WeekKpi
                        :label="isWeaners ? $t('views.weekOverview.summary.totalSoldAndTransferred') : $t('views.weekOverview.summary.totalSlaughtered')"
                        :value="isWeaners ? selectedBatchSoldAndTransferred : selectedBatch.totalSlaughtered"
                        :suffix="$t('views.weekOverview.texts.pcsUnit')"
                        :icon-id="kpiIcons?.totalSlaughteredIconId"
                    />
                    <WeekKpi :label="$t('views.weekOverview.summary.totalDead')" :value="selectedBatch.totalDead" :suffix="$t('views.weekOverview.texts.pcsUnit')" :icon-id="kpiIcons?.totalDeadIconId" />
                    <WeekKpi :label="$t('views.weekOverview.summary.remaining')" :value="selectedBatchRemaining" :suffix="$t('views.weekOverview.texts.pcsUnit')" :icon-id="kpiIcons?.remainingIconId" />
                    <WeekKpi :label="$t('views.weekOverview.summary.avgGain')" :value="selectedBatchAvgGainKg" suffix="kg" :icon-id="kpiIcons?.avgGainIconId" />
                    <WeekKpi :label="$t('views.weekOverview.summary.avgDailyGain')" :value="selectedBatch.dailyGain" suffix="g" :icon-id="kpiIcons?.dailyGainIconId" />
                    <WeekKpi :label="$t('views.weekOverview.summary.avgMortality')" :value="selectedBatchMortalityPercent" suffix="%" :icon-id="kpiIcons?.mortalityIconId" />
                    <WeekKpi :label="$t('views.weekOverview.summary.avgProductionDays')" :value="selectedBatch.productionDays" :fixed-decimals="1" :suffix="$t('views.weekOverview.texts.daysUnit')" :icon-id="kpiIcons?.productionDaysIconId" />
                </div>

                <!-- Life cycle chart -->
                <LifeCycleChart
                    :details="selectedBatch.details"
                    :inserted-date="`${formatDate(selectedBatch.firstInsertionDate)} – ${formatDate(sundayOf(selectedBatch.mondayOfWeek))}`"
                    :inserted-count="selectedBatch.totalInserted"
                    :avg-weight="selectedBatch.avgWeight"
                    :is-weaners="isWeaners"
                />

                <!-- Life cycle table -->
                <div class="rounded-lg border border-gray-200 bg-white p-4">
                    <div class="mb-3 text-sm font-semibold text-gray-700">{{ $t('views.weekOverview.lifeCycle.tableTitle') }}</div>
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                            <thead class="bg-gray-50 text-xs font-semibold text-gray-500">
                                <tr>
                                    <th class="px-3 py-2 text-left">{{ $t('views.weekOverview.lifeCycle.tableHeadings.week') }}</th>
                                    <th class="px-3 py-2 text-left">{{ $t('views.weekOverview.lifeCycle.tableHeadings.date') }}</th>
                                    <th class="px-3 py-2 text-right">{{ $t('views.weekOverview.lifeCycle.tableHeadings.dead') }}</th>
                                    <th class="px-3 py-2 text-right">{{ $t('views.weekOverview.lifeCycle.tableHeadings.soldTransferred') }}</th>
                                    <th v-if="!isWeaners" class="px-3 py-2 text-right">{{ $t('views.weekOverview.lifeCycle.tableHeadings.slaughtered') }}</th>
                                    <th class="px-3 py-2 text-right">{{ $t('views.weekOverview.lifeCycle.tableHeadings.remaining') }}</th>
                                    <th class="px-3 py-2 text-right">
                                        {{ $t(isWeaners ? 'views.weekOverview.lifeCycle.tableHeadings.avgExitWeight' : 'views.weekOverview.lifeCycle.tableHeadings.avgSlaughterWeight') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr v-for="(detail, index) in selectedBatch.details" :key="detail.mondayOfDate">
                                    <td class="px-3 py-2 tabular-nums">{{ index }}</td>
                                    <td class="px-3 py-2 tabular-nums">{{ formatDate(detail.mondayOfDate) }}</td>
                                    <td class="px-3 py-2 text-right tabular-nums">{{ $n(detail.dead ?? 0) }}</td>
                                    <td class="px-3 py-2 text-right tabular-nums">{{ $n(soldAndTransferred(detail) || 0) }}</td>
                                    <td v-if="!isWeaners" class="px-3 py-2 text-right tabular-nums">{{ $n(detail.slaughtered ?? 0) }}</td>
                                    <td class="px-3 py-2 text-right tabular-nums">{{ $n(detail.remaining ?? 0) }}</td>
                                    <td v-if="isWeaners" class="px-3 py-2 text-right tabular-nums">{{ exitCount(detail) > 0 ? $n(detail.avgExitWeight ?? 0, 'decimal') : '—' }}</td>
                                    <td v-else class="px-3 py-2 text-right tabular-nums">{{ detail.slaughtered > 0 ? $n(detail.avgSlaughterWeight ?? 0, 'decimal') : '—' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
        </template>
    </DashboardContentLayout>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { addDays, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import Spinner from '@/components/ui/Spinner.vue';
import AccumulatedStat from './AccumulatedStat.vue';
import WeekKpi from './WeekKpi.vue';
import FarmButtonPicker from './FarmButtonPicker.vue';
import LifeCycleChart from './LifeCycleChart.vue';
import { store } from '@/plugins/store';
import { MutationType } from '@/plugins/store/mutations';
import { useWeekOverviewData } from '@/plugins/store/actions/queries/week-overview';
import { i18n } from '@/plugins/internationalization/i18n';
import { PigType } from '@/types';
import type { WeekDataDetails } from '@/types';

const isWeaners = computed(() => store.state.pigType === PigType.Weaners);

// Farms in the global scope with at least one barn of the active pig type - same "valid farms"
// definition used by /monitoring/section-status's farm picker.
const validFarms = computed(() =>
    store.state.farms.filter(
        (farm) => store.state.selectedFarmIds.includes(farm.farm_id) && store.state.barns.some((barn) => barn.farm_id === farm.farm_id && barn.is_weaners === isWeaners.value)
    )
);

// Kept in the store so it survives navigating away and back, but only honoured while the pig type
// and period it was chosen under are unchanged. A directory switch reloads the app, clearing it.
const selectedFarmId = computed<number | null>({
    get() {
        const saved = store.state.weekOverviewFarm;
        if (
            saved &&
            saved.pigType === store.state.pigType &&
            saved.period === store.state.periodPickerDateString &&
            validFarms.value.some((farm) => farm.farm_id === saved.farmId)
        ) {
            return saved.farmId;
        }
        return null;
    },
    set(farmId) {
        store.commit(MutationType.SetWeekOverviewFarm, farmId === null ? null : { farmId, pigType: store.state.pigType, period: store.state.periodPickerDateString });
    },
});

// Drop the selection if it falls out of scope (global farm selection or pig-type toggle changed);
// never auto-selects a farm - the page starts with none chosen.
watch(validFarms, (farms) => {
    if (selectedFarmId.value !== null && !farms.some((farm) => farm.farm_id === selectedFarmId.value)) {
        selectedFarmId.value = null;
    }
});

const { data: weekOverviewResponse, isLoading } = useWeekOverviewData(selectedFarmId, isWeaners);
const weekData = computed(() => weekOverviewResponse.value?.data ?? []);
const kpiIcons = computed(() => weekOverviewResponse.value?.icons);

// Client-side filter by the period picker's selected range, applied to each batch's insertion
// date only - a batch's own life-cycle data (Details) is never clipped at the range's end date.
// Analytics-v2 already returns the farm's whole history in one call; there is no
// date-range parameter to the API for this reason.
const inRangeBatches = computed(() => {
    const periodParts = store.state.periodPickerDateString.split(' - ');
    if (periodParts.length !== 2) return [];
    const from = new Date(new Date(Date.parse(periodParts[0])).setHours(0, 0, 0, 0));
    const to = new Date(new Date(Date.parse(periodParts[1])).setHours(23, 59, 59, 999));

    return weekData.value
        .filter((batch) => {
            const insertionDate = new Date(batch.firstInsertionDate);
            return insertionDate >= from && insertionDate <= to;
        })
        .sort((a, b) => a.mondayOfWeek.localeCompare(b.mondayOfWeek));
});

// Defaults to the most recent batch in range; resets whenever the in-range list itself changes
// (farm/period/pig-type switch), not on every recompute of the same list.
const selectedBatchIndex = ref(0);
// Immediate: a farm restored from the store can already have cached data when this runs.
watch(
    inRangeBatches,
    (batches) => {
        selectedBatchIndex.value = batches.length ? batches.length - 1 : 0;
    },
    { immediate: true }
);

const selectedBatch = computed(() => inRangeBatches.value[selectedBatchIndex.value] ?? null);

const selectedBatchRemaining = computed(() => {
    const details = selectedBatch.value?.details ?? [];
    return details.length ? details[details.length - 1].remaining : selectedBatch.value?.totalInserted ?? 0;
});

// Weaners leave by sale or transfer rather than slaughter; the odd small-slaughter exit
// is folded in so the weaner columns still add up with "remaining".
function soldAndTransferred(detail: WeekDataDetails): number {
    return detail.departed + detail.transferred + (isWeaners.value ? detail.slaughtered : 0);
}

// Animals that left alive this week - the population avgExitWeight is averaged over.
function exitCount(detail: WeekDataDetails): number {
    return detail.departed + detail.transferred + detail.slaughtered;
}

const selectedBatchSoldAndTransferred = computed(() => (selectedBatch.value?.details ?? []).reduce((sum, detail) => sum + soldAndTransferred(detail), 0));

const selectedBatchMortalityPercent = computed(() => {
    const batch = selectedBatch.value;
    if (!batch || batch.totalInserted === 0) return 0;
    return (batch.totalDead / batch.totalInserted) * 100;
});

// Total live-weight gain per animal (kg) = daily gain (g/day) x production days / 1000.
const selectedBatchAvgGainKg = computed(() => {
    const batch = selectedBatch.value;
    if (!batch) return 0;
    return (batch.dailyGain * batch.productionDays) / 1000;
});

const accumulated = computed(() => {
    const batches = inRangeBatches.value;
    const totalInserted = batches.reduce((sum, batch) => sum + batch.totalInserted, 0);
    const totalSlaughtered = batches.reduce((sum, batch) => sum + batch.totalSlaughtered, 0);
    const totalDead = batches.reduce((sum, batch) => sum + batch.totalDead, 0);
    const totalSoldAndTransferred = batches.reduce((sum, batch) => sum + batch.totalDeparted + batch.totalTransferred + (isWeaners.value ? batch.totalSlaughtered : 0), 0);

    // Weighted by animal count: a weighted average of a per-batch rate, weighted by
    // its own denominator, reduces to sum-of-numerators / sum-of-denominators.
    const averageDeadPercent = totalInserted > 0 ? (totalDead / totalInserted) * 100 : 0;

    const allDetails = batches.flatMap((batch) => batch.details);
    // Slaughter pigs: slaughter weight over slaughtered animals. Weaners carry no slaughter weight - their
    // exit weight is the live weight of everything that left alive.
    const exitWeightTotal = allDetails.reduce(
        (sum, detail) => sum + (isWeaners.value ? (detail.avgExitWeight ?? 0) * exitCount(detail) : (detail.avgSlaughterWeight ?? 0) * detail.slaughtered),
        0
    );
    const exitCountTotal = allDetails.reduce((sum, detail) => sum + (isWeaners.value ? exitCount(detail) : detail.slaughtered), 0);
    const averageExitWeight = exitCountTotal > 0 ? exitWeightTotal / exitCountTotal : 0;

    return {
        numberOfBatches: batches.length,
        totalInserted,
        totalSlaughtered,
        totalDead,
        totalSoldAndTransferred,
        averageDeadPercent,
        averageExitWeight,
    };
});

// parseISO reads date-only strings as local midnight, so the formatted day is the same in every timezone.
function formatDate(date: string | Date): string {
    return i18n.global.d(typeof date === 'string' ? parseISO(date) : date, 'middle');
}

// A cohort is an insertion week starting on Monday; its last day is the Sunday.
function sundayOf(mondayOfWeek: string): Date {
    return addDays(parseISO(mondayOfWeek), 6);
}
</script>
